import { CalendarClock } from "lucide-react";
import type { CrmTaskCompletionFieldDefinition } from "@ease/shared/crm-task-registry";

export interface CrmTaskCompletionOutcomeOption {
  value: string;
  label: string;
}

export interface CrmTaskCompletionFollowUpOption {
  value: string;
  label: string;
}

export interface CrmTaskCompletionFollowUpPreset {
  label: string;
  days: number;
}

interface CrmTaskCompletionOutcomeFieldsPanelProps {
  outcome: string;
  outcomeOptions: readonly CrmTaskCompletionOutcomeOption[];
  fields: readonly CrmTaskCompletionFieldDefinition[];
  values: Record<string, string>;
  outcomeNotes: string;
  outcomeNotesRequired: boolean;
  onOutcomeChange: (value: string) => void;
  onFieldValueChange: (key: string, value: string) => void;
  onOutcomeNotesChange: (value: string) => void;
}

interface CrmTaskCompletionFollowUpPanelProps {
  nextAction: string;
  followUpDate: string;
  followUpPresets: readonly CrmTaskCompletionFollowUpPreset[];
  createFollowUpTask: boolean;
  followUpTaskTitle: string;
  followUpTaskType: string;
  followUpTaskTypeOptions: readonly CrmTaskCompletionFollowUpOption[];
  onNextActionChange: (value: string) => void;
  onFollowUpDateChange: (value: string) => void;
  onFollowUpPresetSelect: (preset: CrmTaskCompletionFollowUpPreset) => void;
  onCreateFollowUpTaskChange: (value: boolean) => void;
  onFollowUpTaskTitleChange: (value: string) => void;
  onFollowUpTaskTypeChange: (value: string) => void;
}

export function isCrmTaskCompletionFieldRequiredForOutcome(
  field: CrmTaskCompletionFieldDefinition,
  outcome: string,
): boolean {
  return Boolean(field.required || (field.requiredOutcomes as readonly string[] | undefined)?.includes(outcome));
}

export function CrmTaskCompletionOutcomeFieldsPanel({
  outcome,
  outcomeOptions,
  fields,
  values,
  outcomeNotes,
  outcomeNotesRequired,
  onOutcomeChange,
  onFieldValueChange,
  onOutcomeNotesChange,
}: CrmTaskCompletionOutcomeFieldsPanelProps) {
  return (
    <>
      <label className="block text-sm">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Outcome</span>
        <select
          value={outcome}
          onChange={(event) => onOutcomeChange(event.target.value)}
          aria-label="Completion outcome"
          className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          {outcomeOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      {fields.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {fields.map((field) => {
            const required = isCrmTaskCompletionFieldRequiredForOutcome(field, outcome);
            return (
              <label key={field.key} className="block text-sm">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                  {field.label}{required ? " *" : ""}
                </span>
                {field.type === "select" ? (
                  <select
                    value={values[field.key] ?? ""}
                    onChange={(event) => onFieldValueChange(field.key, event.target.value)}
                    required={required}
                    aria-label={field.label}
                    className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  >
                    <option value="">Select...</option>
                    {(field.options ?? []).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type === "date" ? "date" : "text"}
                    value={values[field.key] ?? ""}
                    onChange={(event) => onFieldValueChange(field.key, event.target.value)}
                    required={required}
                    aria-label={field.label}
                    className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                )}
              </label>
            );
          })}
        </div>
      )}
      <label className="block text-sm">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
          What happened{outcomeNotesRequired ? " *" : ""}
        </span>
        <textarea
          value={outcomeNotes}
          onChange={(event) => onOutcomeNotesChange(event.target.value)}
          rows={3}
          required={outcomeNotesRequired}
          placeholder="Capture the result, objection, promise, or useful context for the next person."
          className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)]"
        />
      </label>
    </>
  );
}

export function CrmTaskCompletionFollowUpPanel({
  nextAction,
  followUpDate,
  followUpPresets,
  createFollowUpTask,
  followUpTaskTitle,
  followUpTaskType,
  followUpTaskTypeOptions,
  onNextActionChange,
  onFollowUpDateChange,
  onFollowUpPresetSelect,
  onCreateFollowUpTaskChange,
  onFollowUpTaskTitleChange,
  onFollowUpTaskTypeChange,
}: CrmTaskCompletionFollowUpPanelProps) {
  return (
    <>
      <div className="grid gap-3 md:grid-cols-[1fr_180px]">
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Next action</span>
          <input
            type="text"
            value={nextAction}
            onChange={(event) => onNextActionChange(event.target.value)}
            placeholder="e.g. send proposal, schedule call, wait for reply"
            className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)]"
          />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Follow-up date</span>
          <input
            type="date"
            value={followUpDate}
            onChange={(event) => onFollowUpDateChange(event.target.value)}
            className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {followUpPresets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => onFollowUpPresetSelect(preset)}
                aria-label={`Set follow-up date: ${preset.label}`}
                className="inline-flex h-8 items-center justify-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-background)]"
              >
                <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{preset.label}</span>
              </button>
            ))}
          </div>
        </label>
      </div>
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Create next task</span>
            <span className="mt-1 block text-xs text-[var(--color-muted-foreground)]">
              Adds a real follow-up to the queue instead of leaving the next step only in notes.
            </span>
          </span>
          <input
            type="checkbox"
            checked={createFollowUpTask}
            onChange={(event) => onCreateFollowUpTaskChange(event.target.checked)}
            disabled={!followUpDate}
            aria-label="Create follow-up task"
            className="h-4 w-4 rounded border-[var(--color-border)]"
          />
        </label>
        {createFollowUpTask && (
          <div className="mt-3 grid gap-3 md:grid-cols-[1fr_160px]">
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Follow-up title</span>
              <input
                type="text"
                value={followUpTaskTitle}
                onChange={(event) => onFollowUpTaskTitleChange(event.target.value)}
                className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Type</span>
              <select
                value={followUpTaskType}
                onChange={(event) => onFollowUpTaskTypeChange(event.target.value)}
                aria-label="Follow-up task type"
                className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              >
                {followUpTaskTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>
    </>
  );
}
